// The tickets-tab heatmap model — pure, so every rule the room reads off the
// screen is unit-testable (design handoff §4.2, prototype `Tickets B - Desk
// board.dc.html`).
//
// Y = the six desk states in two bands. The split is the point of the chart:
// either the ball is with us or it is not.
// X = a switchable axis whose default is AGE — and **age is time since LAST
// ACTIVITY, not since created** (Isaac's explicit override of the handoff). A
// four-month-old ticket in live correspondence is ACTIVE and must land in an
// early bucket; the server computes `idleDays` from the ball-flip / newest
// comment / last transition and this module only buckets it.
//
// No colour lives here — see `deskCellColor` in DeskHeatmap for the token-bound
// paint. This file returns intensities, counts and selections only.

import type { DeskState, UrgentTicket } from "../lib/api";

export type DeskAxis = "age" | "priority" | "customer" | "owner";

export interface DeskBand {
  key: "us" | "moving";
  label: string;
  states: DeskState[];
}

/** The two bands, top to bottom, with the rule drawn between them. */
export const DESK_BANDS: DeskBand[] = [
  { key: "us", label: "BALL WITH US", states: ["needs_reply", "needs_decision", "untriaged"] },
  {
    key: "moving",
    label: "MOVING WITHOUT US",
    states: ["with_developers", "waiting_on_customer", "ready_to_close"],
  },
];

export const DESK_STATE_LABEL: Record<DeskState, string> = {
  needs_reply: "Needs a reply",
  needs_decision: "Needs a decision",
  untriaged: "Untriaged",
  with_developers: "With developers",
  waiting_on_customer: "Waiting on customer",
  ready_to_close: "Ready to close",
};

/** Dot tone per state (mapped to brainTokens by the renderer, never a hex here). */
export type StateTone = "danger" | "warning" | "teal" | "neutral" | "success";

export const DESK_STATE_TONE: Record<DeskState, StateTone> = {
  needs_reply: "danger",
  needs_decision: "danger",
  untriaged: "warning",
  with_developers: "teal",
  waiting_on_customer: "neutral",
  ready_to_close: "success",
};

const BALL_WITH_US: DeskState[] = DESK_BANDS[0].states;

export function isBallWithUs(state: DeskState): boolean {
  return BALL_WITH_US.includes(state);
}

/** Age buckets, in order. Boundaries are days since LAST ACTIVITY. */
export const AGE_COLUMNS = ["< 1D", "1–3D", "3–7D", "1–2W", "2W+"];

export function ageBucket(idleDays: number): number {
  if (idleDays < 1) return 0;
  if (idleDays < 3) return 1;
  if (idleDays < 7) return 2;
  if (idleDays < 14) return 3;
  return 4;
}

export const PRIORITY_COLUMNS = ["P1", "P2", "P3"];

export interface DeskAxisDef {
  key: DeskAxis;
  label: string;
  columns: string[];
  /** Only the Age axis ramps colour left-to-right (time is ordinal; owners aren't). */
  weighted: boolean;
  bucket: (t: UrgentTicket) => number;
}

const OTHER = "OTHER";
const NOBODY = "NOBODY";

/** The owner axis value for one ticket. Unassigned is a value, not a gap. */
export function ownerOf(t: UrgentTicket): string {
  return (t.assigneeName ?? "").trim() || NOBODY;
}

/** The N busiest values on a categorical axis, plus a catch-all last column. */
export function topCategories(
  tickets: UrgentTicket[],
  pick: (t: UrgentTicket) => string | undefined,
  limit: number,
  catchAll: string
): string[] {
  const counts = new Map<string, number>();
  for (const t of tickets) {
    const v = (pick(t) || "").trim();
    if (!v) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const top = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([v]) => v);
  return [...top, catchAll];
}

/** Force `pin` into a derived column list, dropping the least-busy value to
 *  keep the width. Used when the side rail pivots onto a specific customer —
 *  landing them in OTHER would answer a different question than the one asked. */
export function pinColumn(columns: string[], pin: string | undefined, catchAll: string): string[] {
  if (!pin || columns.includes(pin)) return columns;
  const top = columns.filter((v) => v !== catchAll);
  return [pin, ...top.slice(0, Math.max(0, top.length - 1)), catchAll];
}

/**
 * Build the four axes over the CURRENT ticket set. Customer/Owner columns are
 * derived from the data rather than hardcoded, so a new customer appears the
 * day they raise a ticket.
 */
export function buildAxes(
  tickets: UrgentTicket[],
  opts: { pinCustomer?: string } = {}
): Record<DeskAxis, DeskAxisDef> {
  const customers = pinColumn(
    topCategories(tickets, (t) => t.customer ?? t.account, 4, OTHER),
    opts.pinCustomer,
    OTHER
  );
  // "Unassigned" is a REAL owner value, not the leftovers bin.
  //
  // The prototype's owner axis ends in a `NOBODY` column, which is right only
  // when every actual owner already has a column of their own. Used as the
  // catch-all it states something false: on the live desk 39 tickets that ARE
  // assigned (Desrae 17 · Aeronet Support 14 · Asha 5 · Isaac 3) landed under a
  // heading that says nobody owns them, alongside just 8 genuinely unassigned —
  // and Desrae's seventeen vanished from the chart entirely. So unassigned
  // tickets compete for a column like anybody else, and the leftovers bin is
  // called what it is.
  const owners = topCategories(tickets, ownerOf, 4, OTHER);
  const indexOr = (cols: string[], value: string | undefined, fallbackIndex: number) => {
    const i = value ? cols.indexOf(value) : -1;
    return i < 0 ? fallbackIndex : i;
  };
  return {
    age: {
      key: "age",
      label: "Age",
      columns: AGE_COLUMNS,
      weighted: true,
      bucket: (t) => ageBucket(t.idleDays ?? 0),
    },
    priority: {
      key: "priority",
      label: "Priority",
      columns: PRIORITY_COLUMNS,
      weighted: false,
      bucket: (t) => Math.max(0, PRIORITY_COLUMNS.indexOf(t.bucket ?? "P3")),
    },
    customer: {
      key: "customer",
      label: "Customer",
      columns: customers,
      weighted: false,
      bucket: (t) => indexOr(customers, (t.customer ?? t.account)?.trim(), customers.length - 1),
    },
    owner: {
      key: "owner",
      label: "Owner",
      columns: owners,
      weighted: false,
      bucket: (t) => indexOr(owners, ownerOf(t), owners.length - 1),
    },
  };
}

/**
 * What a whole-row selection spans, in the words of the axis actually on
 * screen. A row on the Owner axis covers every OWNER, not "all ages" — saying
 * "all ages" there tells the room the chart is doing something it is not.
 */
export function allColumnsLabel(axis: DeskAxis): string {
  switch (axis) {
    case "age":
      return "all ages";
    case "priority":
      return "all priorities";
    case "customer":
      return "all customers";
    case "owner":
      return "all owners";
  }
}

/**
 * How honest the delta column is allowed to sound.
 *
 * The baseline is frozen on the FIRST open of its day, so a `Δ 1D` label can be
 * covering anything from a few hours to a full day. Say which.
 */
export function deltaBaselineNote(
  baselineDate: string | undefined,
  baselineAt: number | undefined,
  hasDeltas: boolean
): string {
  if (!hasDeltas) return "No baseline yet — the first snapshot was taken today, so there is nothing to compare against.";
  const when = baselineAt
    ? new Date(baselineAt).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : baselineDate ?? "yesterday";
  return `Change since the single snapshot frozen at ${when} — one day of history, not a trend.`;
}

export interface DeskMatrix {
  columns: string[];
  /** state -> per-column counts. */
  cells: Record<DeskState, number[]>;
  rowTotals: Record<DeskState, number>;
  columnTotals: number[];
  bandTotals: Record<"us" | "moving", number>;
  grandTotal: number;
  maxCell: number;
}

export function buildMatrix(tickets: UrgentTicket[], axis: DeskAxisDef): DeskMatrix {
  const n = axis.columns.length;
  const cells = {} as Record<DeskState, number[]>;
  for (const band of DESK_BANDS) for (const s of band.states) cells[s] = new Array(n).fill(0);

  for (const t of tickets) {
    const state = t.deskState;
    if (!state || !cells[state]) continue;
    const ci = Math.min(n - 1, Math.max(0, axis.bucket(t)));
    cells[state][ci] += 1;
  }

  const rowTotals = {} as Record<DeskState, number>;
  let maxCell = 1;
  for (const s of Object.keys(cells) as DeskState[]) {
    rowTotals[s] = cells[s].reduce((a, b) => a + b, 0);
    for (const v of cells[s]) if (v > maxCell) maxCell = v;
  }
  const columnTotals = new Array(n).fill(0).map((_, ci) =>
    (Object.keys(cells) as DeskState[]).reduce((a, s) => a + cells[s][ci], 0)
  );
  const bandTotals = {
    us: DESK_BANDS[0].states.reduce((a, s) => a + rowTotals[s], 0),
    moving: DESK_BANDS[1].states.reduce((a, s) => a + rowTotals[s], 0),
  };
  return {
    columns: axis.columns,
    cells,
    rowTotals,
    columnTotals,
    bandTotals,
    grandTotal: bandTotals.us + bandTotals.moving,
    maxCell,
  };
}

/**
 * Cell alpha, exactly as the chosen prototype computes it.
 *
 *   ball-with-us : 0.16 + weight·0.55 + magnitude·0.5
 *   moving       : 0.16 + magnitude·0.62, then halved when painted teal
 *
 * `weight` is `(columnIndex + 1) / columnCount` on the AGE axis — the ramp that
 * darkens the right-hand (older) columns — and a flat 0.55 elsewhere, because
 * "P2" is not further along anything. Capped at 0.94 so the darkest cell still
 * reads as a colour rather than a black hole. A zero cell has no fill at all.
 */
export function cellIntensity(opts: {
  count: number;
  columnIndex: number;
  columnCount: number;
  maxCell: number;
  ballWithUs: boolean;
  weighted: boolean;
}): number {
  const { count, columnIndex, columnCount, maxCell, ballWithUs, weighted } = opts;
  if (count === 0) return 0;
  const weight = weighted ? (columnIndex + 1) / columnCount : 0.55;
  const magnitude = count / Math.max(1, maxCell);
  const raw = ballWithUs ? weight * 0.55 + magnitude * 0.5 : magnitude * 0.62;
  return Math.min(0.94, 0.16 + raw);
}

/** Text flips to white on the deep red cells only, above the prototype's 0.52. */
export function cellTextIsWhite(intensity: number, ballWithUs: boolean): boolean {
  return ballWithUs && intensity > 0.52;
}

export interface DeskSelection {
  state: DeskState;
  /** null = the whole row (all columns). */
  column: number | null;
}

/**
 * The hottest cell — the page must arrive with an opinion, never blank.
 *
 * Ranked with Jay's deterministic engine (`ticket-scoring.ts`, surfaced as
 * `ticket.score`): sum the scores of the tickets in each ball-with-us cell and
 * take the highest. There is deliberately no second scoring rule here. Falls
 * back to the biggest ball-with-us cell if no ticket carries a score, and to
 * the biggest cell overall if the top band is empty.
 */
export function hottestCell(
  tickets: UrgentTicket[],
  axis: DeskAxisDef,
  matrix: DeskMatrix
): DeskSelection | null {
  if (!matrix.grandTotal) return null;
  const n = axis.columns.length;
  let best: { state: DeskState; column: number; score: number; count: number } | null = null;

  for (const band of DESK_BANDS) {
    for (const state of band.states) {
      for (let ci = 0; ci < n; ci += 1) {
        const count = matrix.cells[state][ci];
        if (!count) continue;
        const score = tickets
          .filter((t) => t.deskState === state && axis.bucket(t) === ci)
          .reduce((a, t) => a + (t.score ?? 0), 0);
        const candidate = { state, column: ci, score, count };
        if (!best) {
          best = candidate;
          continue;
        }
        // Ball-with-us always outranks moving-without-us: the room only acts on
        // the top band, so the default selection must land there.
        const bestUs = isBallWithUs(best.state);
        const candUs = isBallWithUs(state);
        if (candUs !== bestUs) {
          if (candUs) best = candidate;
          continue;
        }
        if (candidate.score > best.score || (candidate.score === best.score && candidate.count > best.count)) {
          best = candidate;
        }
      }
    }
  }
  return best ? { state: best.state, column: best.column } : null;
}

/** The tickets in a selection, hottest first (same engine, no second score). */
export function selectionTickets(
  tickets: UrgentTicket[],
  axis: DeskAxisDef,
  selection: DeskSelection
): UrgentTicket[] {
  return tickets
    .filter(
      (t) =>
        t.deskState === selection.state &&
        (selection.column === null || axis.bucket(t) === selection.column)
    )
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || a.key.localeCompare(b.key));
}

/** The state-appropriate row actions from the handoff §4.3. */
export function actionsForState(state: DeskState): { label: string; done: string; primary: boolean }[] {
  switch (state) {
    case "needs_decision":
      return [
        { label: "Decide", done: "✓ Decided in standup", primary: true },
        { label: "Book a call", done: "✓ Call booked", primary: false },
      ];
    case "needs_reply":
      return [
        { label: "Reply", done: "✓ Replied", primary: true },
        { label: "Park", done: "✓ Parked — waiting on them", primary: false },
      ];
    case "untriaged":
      return [
        { label: "Bucket it", done: "✓ Bucketed", primary: true },
        { label: "Assign", done: "✓ Assigned", primary: false },
      ];
    default:
      return [{ label: "Open", done: "✓ Opened in Jira", primary: false }];
  }
}

/** `+3` / `-2` / `—`, and which way it should read. */
export interface DeltaRender {
  text: string;
  tone: "danger" | "success" | "muted" | "flat";
}

/**
 * A row's day-over-day movement. `undefined` (no baseline) renders `—` in the
 * flat tone — the honest answer on day one, never a fabricated zero.
 * A ball-with-us row that GREW is bad news (red); anything that shrank is good.
 */
export function deltaRender(state: DeskState, delta: number | undefined): DeltaRender {
  if (delta === undefined || delta === null) return { text: "—", tone: "flat" };
  if (delta === 0) return { text: "—", tone: "flat" };
  if (delta < 0) return { text: String(delta), tone: "success" };
  return { text: `+${delta}`, tone: isBallWithUs(state) ? "danger" : "muted" };
}

/** `3.2h` / `48m` from milliseconds. */
export function formatDuration(ms?: number): string | undefined {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return undefined;
  const hours = ms / 3_600_000;
  if (hours < 1) return `${Math.round(ms / 60_000)}m`;
  return `${(Math.round(hours * 10) / 10).toFixed(1)}h`;
}

/** `4m ago` / `2h ago` / `3d ago` — the sync stamp. */
export function formatAgo(ts?: number, nowMs = Date.now()): string {
  if (!ts) return "just now";
  const mins = Math.max(0, Math.round((nowMs - ts) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export interface CustomerPressure {
  name: string;
  monogram: string;
  open: number;
  withUs: number;
  worstIdleDays: number;
}

/**
 * "Who is having a bad week" — longest silence first. Silence is measured on
 * the tickets whose ball is with US: a customer patiently waiting on a dev fix
 * is not having a bad week because of us.
 */
export function customerPressure(tickets: UrgentTicket[], limit = 5): CustomerPressure[] {
  const byCustomer = new Map<string, UrgentTicket[]>();
  for (const t of tickets) {
    const name = (t.customer ?? t.account ?? "").trim();
    if (!name) continue;
    const arr = byCustomer.get(name) ?? [];
    arr.push(t);
    byCustomer.set(name, arr);
  }
  return [...byCustomer.entries()]
    .map(([name, list]) => {
      const withUsList = list.filter((t) => t.deskState && isBallWithUs(t.deskState));
      return {
        name,
        monogram: monogramFor(name),
        open: list.length,
        withUs: withUsList.length,
        worstIdleDays: withUsList.reduce((m, t) => Math.max(m, t.idleDays ?? 0), 0),
      };
    })
    .sort((a, b) => b.worstIdleDays - a.worstIdleDays || b.withUs - a.withUs || a.name.localeCompare(b.name))
    .slice(0, limit);
}

/** Two-letter monogram from a customer name. */
export function monogramFor(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (!words.length) return "··";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * The agent panel's paragraph. It must NAME TICKETS, not summarise vibes — so
 * it is composed from the real worst offenders and returns null rather than
 * padding when there is nothing concrete to say.
 */
export function deskAgentSummary(
  tickets: UrgentTicket[],
  desk?: { untriaged: number; triagedPendingReview: number } | null
): string | null {
  const withUs = tickets.filter((t) => t.deskState && isBallWithUs(t.deskState));
  if (!withUs.length) return null;
  const worst = withUs
    .slice()
    .sort((a, b) => (b.idleDays ?? 0) - (a.idleDays ?? 0) || (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 2);
  const named = worst
    .map((t) => `${t.key} (${t.idleDays ?? 0}d, ${DESK_STATE_LABEL[t.deskState!].toLowerCase()})`)
    .join(" and ");
  const triageLine =
    desk && desk.triagedPendingReview > 0
      ? ` ${desk.triagedPendingReview} of ${desk.untriaged + desk.triagedPendingReview} untriaged tickets already carry an AI suggestion waiting for a human.`
      : "";
  return `${withUs.length} tickets are sitting with us. The longest silences are ${named}.${triageLine}`;
}
